import React, { useState, useEffect, useRef } from 'react';
import musicSheetMapping from '../data/musicSheetMapping.json';

const MusicSheetViewer = ({ currentTime }) => {
  const [currentMeasure, setCurrentMeasure] = useState(1);
  const [currentPosition, setCurrentPosition] = useState(0);
  const iframeRef = useRef(null);
  
  useEffect(() => {
    // Find the mapping entry that corresponds to the current time
    const findCurrentPosition = () => {
      // Find the mapping entry with the timestamp closest to but not exceeding the current time
      let beforeMapping = null;
      let afterMapping = null;
      
      // Find the mappings before and after the current time
      for (const mapping of musicSheetMapping) {
        if (mapping.timestamp <= currentTime) {
          if (!beforeMapping || mapping.timestamp > beforeMapping.timestamp) {
            beforeMapping = mapping;
          }
        } else {
          if (!afterMapping || mapping.timestamp < afterMapping.timestamp) {
            afterMapping = mapping;
          }
        }
      }
      
      if (beforeMapping) {
        // Set the current measure for display
        setCurrentMeasure(beforeMapping.measure);
        
        let newPosition;
        
        // If we have both before and after mappings, interpolate between them
        if (afterMapping) {
          const timeDiff = afterMapping.timestamp - beforeMapping.timestamp;
          const positionDiff = afterMapping.position - beforeMapping.position;
          
          if (timeDiff > 0) {
            // Calculate the position based on linear interpolation
            const timeRatio = (currentTime - beforeMapping.timestamp) / timeDiff;
            newPosition = beforeMapping.position + (timeRatio * positionDiff);
          } else {
            newPosition = beforeMapping.position;
          }
        } else {
          // If we don't have an after mapping, just use the before mapping position
          newPosition = beforeMapping.position;
        }
        
        setCurrentPosition(newPosition);
        
        // Update the iframe content if it's loaded
        updateIframePosition(newPosition);
      }
    };
    
    findCurrentPosition();
  }, [currentTime]);
  
  // Function to update the iframe's music sheet position
  const updateIframePosition = (position) => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const iframeWindow = iframe.contentWindow;
      
      // Call the setTargetPosition function for smooth scrolling if it exists
      if (iframeWindow && iframeWindow.setTargetPosition) {
        iframeWindow.setTargetPosition(position);
      }
      // Fallback to direct positioning if the smooth function doesn't exist
      else if (iframeWindow && iframeWindow.scrollToPosition) {
        iframeWindow.scrollToPosition(position);
      }
    }
  };
  
  // Set up the iframe reference callback
  const setIframeRef = (iframe) => {
    iframeRef.current = iframe;
    
    // When the iframe loads, update its position
    if (iframe) {
      iframe.onload = () => {
        updateIframePosition(currentPosition);
      };
    }
  };
  
  return (
    <div className="mt-4">
      <div className="bg-gray-800 p-4 rounded-t-lg">
        <h2 className="text-white text-lg font-semibold text-center">Music Sheet - Measure {currentMeasure}</h2>
      </div>
      
      <div className="bg-gray-700 p-2 rounded-b-lg shadow-lg">
        <div className="w-full h-[300px] overflow-hidden relative">
          <iframe
            ref={setIframeRef}
            src="/images/music-sheet/scrolling-sheet.html"
            title="Music Sheet"
            className="w-full h-full"
            style={{ border: 'none' }}
          />
        </div>
        <div className="mt-2 text-sm text-gray-300 text-center">
          <p>The red line shows the current position in the music</p>
        </div>
      </div>
    </div>
  );
};

export default MusicSheetViewer;
